import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { InputIcon, Input, Label, toast } from "keep-react";
import { Lock, Eye, EyeSlash, CheckCircle } from "phosphor-react";
import { ShowPasswordStrength } from "@components/auth/ShowPass";
import { PassCheckList } from "@components/auth/PassCheckList";
import { passwordStrengthChecker } from "@/helpers/checkPasswordStrength";
import {
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
} from "@store/api-helpers/userApi";
import { OTPInput } from "@components/auth/OTPInput";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";

// Step-Enum
enum Step {
  EMAIL = 0,
  OTP = 1,
  PASSWORD = 2,
  SUCCESS = 3,
}

// Props-Interface
export interface HandlePasswordChangeProps {
  onSuccess?: () => void;
}

export const HandlePasswordChange: React.FC<HandlePasswordChangeProps> = ({
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { currentSchema } = useStyleSchema();
  const buttonColor = currentSchema.colors?.primary?.hex;

  // API Hooks
  const [requestPasswordReset, { isLoading: isRequesting }] =
    useRequestPasswordResetMutation();
  const [resetPassword, { isLoading: isResetting }] =
    useResetPasswordMutation();

  // Step-Handling
  const [step, setStep] = useState<Step>(Step.EMAIL);
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState<string>("");

  // Passwort-UX State
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [isMismatch, setIsMismatch] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordContains, setPasswordContains] = useState<string[]>([]);
  const [passwordLength, setPasswordLength] = useState(0);
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  // Live-Password-Check
  const handlePasswordChange = (
    field: "password" | "confirmPassword",
    value: string
  ) => {
    if (field === "password") {
      setPassword(value);
      const result = passwordStrengthChecker(value);
      setPasswordStrength(result.id);
      setPasswordContains(result.contains);
      setPasswordLength(result.length);
      setPasswordError("");
      setIsMismatch(value !== confirmPassword && confirmPassword.length > 0);
      setIsPasswordValid(
        result.id >= 2 &&
          confirmPassword.length > 0 &&
          value === confirmPassword
      );
    } else {
      setConfirmPassword(value);
      setIsMismatch(password !== value && value.length > 0);
      setIsPasswordValid(
        passwordStrength >= 2 && value.length > 0 && password === value
      );
    }
  };

  // UI
  return (
    <div className="w-full flex flex-col items-center space-y-4">
      {step === Step.EMAIL && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!email) return;
            setError(null);
            try {
              await requestPasswordReset({ mail: email }).unwrap();
              setStep(Step.OTP);
              toast.success(t("auth.reset-code-sent"));
            } catch (e: any) {
              setError(e?.data?.message || t("auth-errors.email-not-found"));
              toast.error(e?.data?.message || t("auth-errors.email-not-found"));
            }
          }}
          className="space-y-8 mb-4 w-full flex flex-col items-center"
        >
          <div className="space-y-2 w-full flex flex-col">
            <Label htmlFor="email">{t("resetPassword.title.enterMail")}</Label>
            <Input
              id="email"
              type="email"
              aria-label={t("auth.mail")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
          </div>
          <button
            type="submit"
            disabled={isRequesting}
            className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: buttonColor, color: "white" }}
          >
            {isRequesting ? t("common.loading") : t("auth.request-reset-code")}
          </button>
          {error && <p className="text-red-500">{error}</p>}
        </form>
      )}

      {step === Step.OTP && (
        <OTPInput
          email={email}
          onSuccess={(code) => {
            setOtp(code);
            setStep(Step.PASSWORD);
          }}
          onError={setError}
        />
      )}

      {step === Step.PASSWORD && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            if (!isPasswordValid) {
              setPasswordError(t("auth-errors.password-too-weak"));
              return;
            }
            try {
              await resetPassword({
                mail: email,
                resetCode: otp,
                newPassword: password,
              }).unwrap();
              setStep(Step.SUCCESS);
              toast.success(t("auth.password-reset-success"));
              if (onSuccess) onSuccess();
              setPassword("");
              setConfirmPassword("");
            } catch (e: any) {
              setError(e?.data?.message || t("auth-errors.unknown"));
              toast.error(e?.data?.message || t("auth-errors.unknown"));
            }
          }}
          className="space-y-6 w-full flex flex-col items-center"
        >
          <div className="space-y-2 w-full flex flex-col">
            <Label htmlFor="password">{t("auth.new-password-label")}</Label>
            <div className="relative w-full flex items-center">
              <Input
                id="password"
                type={passwordVisible ? "text" : "password"}
                value={password}
                onChange={(e) => handlePasswordChange("password", e.target.value)}
                autoFocus
                className="ps-11 pe-11"
              />
              <InputIcon>
                <Lock size={19} color="#455468" />
              </InputIcon>
              <InputIcon
                onClick={() => setPasswordVisible((prev) => !prev)}
                className="absolute pointer-events-auto inset-y-0 start-auto end-3 flex items-center cursor-pointer"
              >
                {passwordVisible ? (
                  <EyeSlash size={19} color="#455468" />
                ) : (
                  <Eye size={19} color="#455468" />
                )}
              </InputIcon>
            </div>
            <ShowPasswordStrength strength={passwordStrength as 0 | 1 | 2 | 3} />
            <PassCheckList contains={passwordContains} length={passwordLength} />
          </div>
          <div className="space-y-2 w-full flex flex-col">
            <Label htmlFor="confirmPassword">
              {t("auth.confirm-new-password-label")}
            </Label>
            <div className="relative w-full flex items-center">
              <Input
                id="confirmPassword"
                type={confirmVisible ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) =>
                  handlePasswordChange("confirmPassword", e.target.value)
                }
                className="ps-11 pe-11"
              />
              <InputIcon>
                <Lock size={19} color="#455468" />
              </InputIcon>
              <InputIcon
                onClick={() => setConfirmVisible((prev) => !prev)}
                className="absolute pointer-events-auto inset-y-0 start-auto end-3 flex items-center cursor-pointer"
              >
                {confirmVisible ? (
                  <EyeSlash size={19} color="#455468" />
                ) : (
                  <Eye size={19} color="#455468" />
                )}
              </InputIcon>
            </div>
            {isMismatch && (
              <p className="text-red-500">
                {t("auth-errors.passwords-do-not-match")}
              </p>
            )}
            {passwordError && <p className="text-red-500">{passwordError}</p>}
          </div>
          <button
            type="submit"
            disabled={isResetting || !isPasswordValid}
            className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: buttonColor, color: "white" }}
          >
            {isResetting ? t("common.loading") : t("resetPassword.buttonLabel")}
          </button>
          {error && <p className="text-red-500">{error}</p>}
        </form>
      )}

      {step === Step.SUCCESS && (
        <div className="text-center space-y-4 animate-scaleIn flex flex-col items-center">
          <div 
                  className="w-16 h-16 rounded-full mb-4 flex items-center justify-center"
                  style={{ backgroundColor: buttonColor }}
                >
                  <CheckCircle size={32} weight="bold" className="text-white" />
                </div>
          <h2 className="text-2xl font-bold">
            {t("resetPassword.success.title")}
          </h2>
          <p>{t("resetPassword.success.message")}</p>
          
        </div>
      )}
    </div>
  );
};

export default HandlePasswordChange;
